import { Translation } from '@ps/web-shared-components';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import withWidgetTestId from '@swf/core/hoc/withWidgetTestId';
import useTrackingLabel from '@swf/core/hooks/useTrackingLabel';
import type { WidgetFC } from '@swf/core/types/Widget';
import { EventCard, ExpandablePanel, Icon, Title, TryAgainMessage } from '@swf/sports/components';
import useScoreboardPolling from '@swf/sports/hooks/useScoreboardPolling/useScoreboardPolling';

import { TXTWCL_Live_Data } from './SportsEventInfoWidget.messages.json';
import styles from './SportsEventInfoWidget.scss';
import { DataVis } from './components/DataVis/DataVis';
import LoadingState from './components/LoadingState/LoadingState';
import { sportsEventInfoWidgetModel, useSportsEventInfoWidget } from './model';

interface DataVisPanelProps {
  eventId: number;
  eventTypeId: number;
  name: string;
}

const DataVisPanel: React.FC<DataVisPanelProps> = ({ eventId, eventTypeId, name }) => {
  const { widgetSelect } = useSportsEventInfoWidget();
  const isDataVisAvailable = useSelector(widgetSelect.isDataVisAvailable);
  const trackingLabel = useTrackingLabel(TXTWCL_Live_Data);

  if (!isDataVisAvailable) return null;

  return (
    <ExpandablePanel
      className={styles.dataVisContainer}
      openInitially
      panelName={trackingLabel}
      title={
        <Title isDataVis>
          <span className={styles.dataVisTitle}>
            <Icon name="in-play" size="lg" className={styles.dataVisTitleIcon} />
            <span>
              <Translation {...TXTWCL_Live_Data} />
            </span>
          </span>
        </Title>
      }
    >
      <DataVis
        eventId={eventId}
        name={name}
        eventTypeId={eventTypeId}
        data-testid="data-vis-iframe"
      />
    </ExpandablePanel>
  );
};

const SportsEventInfoWidget: WidgetFC = () => {
  const { widgetSelect } = useSportsEventInfoWidget();
  const eventInfo = useSelector(widgetSelect.eventInfo);
  const isLoading = useSelector(widgetSelect.isLoading);
  const errorCode = useSelector(widgetSelect.errorCode);
  const hasPreloaded = useSelector(widgetSelect.hasPreloaded);

  const eventTypesWithEventIds = useMemo(
    () =>
      eventInfo && eventInfo.inPlay
        ? {
            [eventInfo.eventTypeId]: [eventInfo.eventId]
          }
        : {},
    [eventInfo]
  );

  useScoreboardPolling(eventTypesWithEventIds);

  if (isLoading && !hasPreloaded) {
    return <LoadingState />;
  }

  if (errorCode) {
    if (errorCode === 'NOT_FOUND' && eventInfo) {
      const { offeredInPlay, eventId, eventTypeId, name } = eventInfo;
      return (
        <>
          <EventCard {...eventInfo} canTurnInPlay={offeredInPlay} eventStatusOverride="COMPLETE" />
          <DataVisPanel eventId={eventId} eventTypeId={eventTypeId} name={name} />
        </>
      );
    }

    if (errorCode === 'NOT_FOUND') {
      return null;
    }

    return <TryAgainMessage />;
  }

  if (!eventInfo) {
    return null;
  }

  const { offeredInPlay, eventId, name, ...rest } = eventInfo;

  return (
    <>
      <EventCard eventId={eventId} name={name} {...rest} canTurnInPlay={offeredInPlay} />
      <DataVisPanel eventId={eventId} eventTypeId={eventInfo.eventTypeId} name={name} />
    </>
  );
};

SportsEventInfoWidget.model = sportsEventInfoWidgetModel;

export default withWidgetTestId(SportsEventInfoWidget, 'sports-event-info');
